import { Calendar, User, Tag, ArrowLeft, Clock, Share2, Phone, Mail, PoundSterling, TrendingUp, Briefcase, Target, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function SalaryNegotiation() {
  const shareUrl = window.location.href
  const shareTitle = "How to Negotiate Your Salary as an HGV Driver"

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        })
      } catch (error) {
        console.log('Error sharing:', error)
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-c9-blue to-c9-blue-alt text-white py-16">
        <div className="container-full">
          <div className="flex items-center gap-4 mb-8">
            <Link 
              to="/blog" 
              className="flex items-center gap-2 text-c9-orange hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>6th June 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>8 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Sam Wharfe (CEO, C9 Recruitment)</span>
              </div>
              <span className="bg-c9-orange px-3 py-1 rounded-full text-xs font-medium">Career Advice</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
              How to Negotiate Your <span className="text-c9-orange">Salary</span> as an HGV Driver
            </h1>
            
            <p className="text-xl mb-8 leading-relaxed opacity-90">
              With HGV driver demand remaining strong and average salaries rising across the UK, now is the perfect time to ensure you're being paid what you're worth. Here's how to approach salary negotiations professionally and effectively.
            </p>
            
            <div className="flex items-center gap-4">
              <button 
                onClick={handleShare}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
              >
                <Share2 className="h-4 w-4" />
                Share Article
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="container-full">
          <div className="article-content">
            <div className="article-prose">
              
              <h2 className="flex items-center gap-3">
                <TrendingUp className="h-6 w-6 text-c9-orange" />
                The Current Market Reality
              </h2>
              
              <p>The UK transport industry continues to experience strong demand for qualified drivers. According to recent industry data, driver vacancy rates have stabilised but remain elevated, creating opportunities for negotiation and career advancement.</p>
              
              <p>Average HGV driver salaries have increased significantly, with many companies offering improved packages to attract and retain talent. This market dynamic puts experienced drivers in a strong negotiating position.</p>

              <div className="article-highlight">
                <div className="flex items-start gap-3">
                  <PoundSterling className="h-6 w-6 text-c9-orange mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-c9-blue mb-2">Market Insight</h3>
                    <p className="text-gray-700">The average HGV driver salary has increased by 15-20% over the past two years, with experienced drivers commanding even higher premiums in specialist sectors.</p>
                  </div>
                </div>
              </div>

              <h2>Understanding Your Value</h2>
              
              <p>Before entering any negotiation, you need to understand your market value. Several factors influence driver salaries:</p>
              
              <h3>Experience and Track Record</h3>
              <ul>
                <li><strong>Years of experience:</strong> More experienced drivers command higher salaries</li>
                <li><strong>Safety record:</strong> Clean licence and accident-free history are valuable</li>
                <li><strong>Reliability:</strong> Consistent attendance and punctuality matter</li>
                <li><strong>Customer feedback:</strong> Positive relationships with clients add value</li>
              </ul>

              <h3>Qualifications and Endorsements</h3>
              <ul>
                <li><strong>Basic Category C+E:</strong> Standard HGV licence</li>
                <li><strong>ADR certification:</strong> Hazardous goods – significant salary premium</li>
                <li><strong>HIAB/crane operation:</strong> Specialist equipment operation</li>
                <li><strong>Driver CPC:</strong> Essential compliance requirement</li>
                <li><strong>Additional training:</strong> Customer service, first aid, etc.</li>
              </ul>

              <h3>Regional and Sector Variations</h3>
              <ul>
                <li><strong>London/South East:</strong> Higher rates but increased living costs</li>
                <li><strong>Regional variations:</strong> Local market conditions affect pay scales</li>
                <li><strong>Sector specialisation:</strong> Construction, retail, hazardous goods vary significantly</li>
                <li><strong>Contract type:</strong> Permanent vs agency vs owner-operator</li>
              </ul>

              <h2>Preparation is Key</h2>
              
              <h3>Research Market Rates</h3>
              <p>Before any negotiation, research current market rates for your role:</p>
              <ul>
                <li>Check job advertisements for similar positions</li>
                <li>Speak to other drivers in your network</li>
                <li>Consult recruitment agencies for market intelligence</li>
                <li>Consider geographic and sector variations</li>
                <li>Factor in benefits as well as base salary</li>
              </ul>

              <h3>Document Your Value</h3>
              <p>Prepare evidence of your worth to the company:</p>
              <ul>
                <li>Safety record and clean driving licence</li>
                <li>Training certificates and qualifications</li>
                <li>Customer feedback and testimonials</li>
                <li>Attendance and reliability records</li>
                <li>Any additional responsibilities you've taken on</li>
              </ul>

              <h2>Negotiation Strategies</h2>
              
              <h3>Choose the Right Moment</h3>
              <p>Timing is crucial for successful negotiations:</p>
              <ul>
                <li><strong>Annual reviews:</strong> Natural opportunity for discussion</li>
                <li><strong>After completing additional training:</strong> Enhanced value proposition</li>
                <li><strong>Following exceptional performance:</strong> Leverage recent achievements</li>
                <li><strong>Company success periods:</strong> When business is performing well</li>
                <li><strong>Market changes:</strong> When industry rates have increased</li>
              </ul>

              <h3>Professional Approach</h3>
              <ul>
                <li>Request a formal meeting rather than casual conversation</li>
                <li>Present facts and market data, not emotions</li>
                <li>Focus on your value to the business</li>
                <li>Be prepared to discuss alternatives to salary increases</li>
                <li>Listen to employer's perspective and constraints</li>
              </ul>

              <div className="article-warning">
                <div className="flex items-start gap-3">
                  <Target className="h-6 w-6 text-yellow-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-yellow-800 mb-2">Negotiation Tip</h3>
                    <p className="text-yellow-700">Never make salary negotiations personal or emotional. Focus on business value, market rates, and professional development. This approach maintains good relationships regardless of outcome.</p>
                  </div>
                </div>
              </div>

              <h2>Beyond Base Salary</h2>
              
              <p>If base salary increases aren't possible, consider negotiating other benefits:</p>
              
              <h3>Financial Benefits</h3>
              <ul>
                <li><strong>Performance bonuses:</strong> Safety, fuel efficiency, customer service bonuses</li>
                <li><strong>Overtime rates:</strong> Enhanced rates for extra hours</li>
                <li><strong>Pension contributions:</strong> Improved employer contributions</li>
                <li><strong>Fuel cards:</strong> Personal use allowances</li>
                <li><strong>Health insurance:</strong> Private medical coverage</li>
              </ul>

              <h3>Work-Life Balance</h3>
              <ul>
                <li><strong>Additional holidays:</strong> Extra annual leave days</li>
                <li><strong>Flexible scheduling:</strong> Better work patterns</li>
                <li><strong>Home time guarantees:</strong> Minimum time at home</li>
                <li><strong>Route preferences:</strong> Preferred geographic areas</li>
                <li><strong>Equipment standards:</strong> Better vehicle specifications</li>
              </ul>

              <h2>Timing Your Approach</h2>
              
              <h3>Annual Reviews</h3>
              <p>Come prepared with documentation of your achievements, safety record, and any additional training completed. If the company has had a good year, this strengthens your position.</p>
              
              <h3>Competing Offers</h3>
              <p>If you receive a better offer elsewhere, approach your current employer professionally. They may match or exceed the offer to retain you, especially given current driver shortages.</p>

              <h2>What to Do If the Answer Is No</h2>
              
              <p>If your request is declined, don't take it personally. Ask:</p>
              <ul>
                <li>What would need to change for a salary increase to be possible?</li>
                <li>When would be an appropriate time to revisit the conversation?</li>
                <li>Are there alternative benefits that could be considered?</li>
                <li>What development opportunities exist to increase your value?</li>
              </ul>

              <h2>Industry-Specific Considerations</h2>
              
              <h3>Different Sectors, Different Rates</h3>
              <div className="article-card">
                <h4 className="font-bold text-c9-blue mb-3">Salary Ranges by Sector (2025)</h4>
                <ul className="space-y-2">
                  <li><strong>General haulage:</strong> £28,000-£35,000</li>
                  <li><strong>Hazardous materials (ADR):</strong> £32,000-£42,000</li>
                  <li><strong>Specialist transport:</strong> £35,000-£45,000</li>
                  <li><strong>Local delivery:</strong> £26,000-£32,000</li>
                  <li><strong>Long-haul European:</strong> £35,000-£50,000</li>
                </ul>
              </div>

              <h3>Regional Variations</h3>
              <p>London and the South East typically offer the highest rates, but factor in living costs. Scotland, Wales, and Northern England may offer lower base salaries but better value for money.</p>

              <h2>Professional Support</h2>
              
              <p>Consider working with recruitment specialists who understand the market and can provide valuable insights into fair compensation packages. Agencies like C9 Recruitment have detailed knowledge of current pay rates and can help you find employers offering competitive packages.</p>
              
              <p>We regularly negotiate on behalf of candidates and can provide market intelligence to help you make informed decisions about your career moves.</p>

              <h2>Long-Term Career Planning</h2>
              
              <p>Remember that salary negotiation is part of broader career planning:</p>
              <ul>
                <li>Invest in additional training and certifications</li>
                <li>Build a reputation for reliability and professionalism</li>
                <li>Consider specialising in high-demand areas</li>
                <li>Maintain an excellent safety record</li>
                <li>Network within the industry</li>
              </ul>

              <div className="article-success">
                <div className="flex items-start gap-3">
                  <Briefcase className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-green-800 mb-2">Career Success</h3>
                    <p className="text-green-700">Successful salary negotiation is about building long-term relationships and demonstrating ongoing value. Approach discussions professionally and focus on mutual benefit.</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="bg-c9-blue text-white section-padding">
        <div className="container-full text-center">
          <div className="max-w-3xl mx-auto">
            <div className="article-cta">
              <h3 className="text-2xl font-bold mb-4 flex items-center justify-center gap-2">
                <PoundSterling className="h-6 w-6" />
                Ready to Explore Better Opportunities?
              </h3>
              <p className="text-lg mb-6">
                Our recruitment specialists can help you find roles with competitive salaries and excellent benefits packages. We provide market intelligence and negotiate on your behalf.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/candidates" 
                  className="flex items-center justify-center gap-2 bg-white text-c9-orange hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors"
                >
                  <ExternalLink className="h-5 w-5" />
                  Browse High-Paying Opportunities
                </Link>
                <a 
                  href="tel:02081524574" 
                  className="flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-c9-blue px-8 py-3 rounded-lg font-medium transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  Salary Discussion Support
                </a>
              </div>
              
              <div className="mt-8 text-sm text-gray-200">
                <p>Get expert advice on salary negotiation and market rates</p>
                <div className="flex items-center justify-center gap-6 mt-3">
                  <a href="tel:02081524574" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone className="h-4 w-4" />
                    0208 152 4574
                  </a>
                  <a href="mailto:commercial@c9-recruitment.com" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Mail className="h-4 w-4" />
                    commercial@c9-recruitment.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
